import { useCallback, useMemo } from "react";
import { Box, Typography, Paper, Stack, Chip, IconButton, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { FoodsIcon, iconMapping } from "@foodpilot/foods";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import { MenuItemDto } from "src/api/client-api/menu/useMenuAdmin";
import { useSnackbar } from "src/utils/useSnackbar";
import { translatedMenuLabel } from "./translatedMenuLabel";

type MenuTreeNodeWithAdmin = {
    item: MenuItemDto;
    children: MenuTreeNodeWithAdmin[];
};

type DraggableMenuTreeProps = {
    menuTree: MenuTreeNodeWithAdmin[];
    expandedItems: Set<number>;
    onToggleExpanded: (itemId: number) => void;
    onEditItem: (item: MenuItemDto) => void;
    onDeleteItem: (itemId: number) => void;
    onReorder: (reorderData: {
        items: Array<{
            id: number;
            weight: number;
            parentId: number | null;
        }>;
    }) => Promise<void>;
    canModify: boolean;
    canDelete: boolean;
};

type SortableMenuItemProps = {
    item: MenuItemDto;
    level: number;
    hasChildren: boolean;
    isExpanded: boolean;
    onToggleExpanded: (itemId: number) => void;
    onEditItem: (item: MenuItemDto) => void;
    onDeleteItem: (itemId: number) => void;
    canModify: boolean;
    canDelete: boolean;
};

const renderFoodsIcon = (name?: string) => {
    const key = name || "menu";
    const alias: Record<string, string> = {
        scoresindicators: "scoresIndicators",
        company: "corporate",
        menu: "hamburgerMenu",
    };
    const resolved = alias[key] || key;
    const available = iconMapping as Record<string, unknown>;
    const safeKey = Object.prototype.hasOwnProperty.call(available, resolved) ? resolved : "default";
    return <FoodsIcon size={2} icon={safeKey as keyof typeof iconMapping} />;
};

const SortableMenuItem = (props: SortableMenuItemProps) => {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.item.id,
        disabled: !props.canModify,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            sx={{
                p: 2,
                mb: 1,
                ml: props.level * 3,
                bgcolor: isDragging ? "action.hover" : "background.paper",
                border: isDragging ? "2px dashed" : "1px solid",
                borderColor: isDragging ? "primary.main" : "divider",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1}>
                {props.canModify && (
                    <Box
                        {...attributes}
                        {...listeners}
                        aria-label={t("menu.item.reorder", "Reorder menu item")}
                        sx={{
                            cursor: "grab",
                            display: "flex",
                            alignItems: "center",
                            "&:active": { cursor: "grabbing" },
                        }}
                    >
                        <DragIcon color="action" />
                    </Box>
                )}

                {props.hasChildren && (
                    <IconButton size="small" onClick={() => props.onToggleExpanded(props.item.id)}>
                        {props.isExpanded ?
                            <ExpandLessIcon />
                        :   <ExpandMoreIcon />}
                    </IconButton>
                )}

                <Box sx={{ display: "flex", alignItems: "center" }}>{renderFoodsIcon(props.item.icon)}</Box>

                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={props.level === 0 ? 600 : 400}>
                        {translatedMenuLabel(t, props.item)}
                    </Typography>
                    {props.item.url && (
                        <Typography variant="body2" color="text.secondary">
                            {props.item.url}
                        </Typography>
                    )}
                </Box>

                <Stack direction="row" spacing={1}>
                    {props.item.moduleFeatureKey && (
                        <Chip label={props.item.moduleFeatureKey} size="small" variant="outlined" color="info" />
                    )}
                    {!props.item.isActive && <Chip label="Inactive" size="small" variant="outlined" color="warning" />}
                    {props.item.permission && (
                        <Chip label={props.item.permission.identifier} size="small" variant="outlined" color="error" />
                    )}
                </Stack>

                <Stack direction="row" spacing={0.5}>
                    {props.canModify && (
                        <IconButton
                            size="small"
                            onClick={() => props.onEditItem(props.item)}
                            color="primary"
                            aria-label={t("menu.item.edit", "Edit menu item")}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                    {props.canDelete && (
                        <IconButton
                            size="small"
                            onClick={() => props.onDeleteItem(props.item.id)}
                            color="error"
                            aria-label={t("menu.item.delete", "Delete menu item")}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
        </Paper>
    );
};

export const DraggableMenuTree = (props: DraggableMenuTreeProps) => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Flatten the tree for DnD context - ALWAYS include all items for drag & drop
    const flattenTree = useCallback(
        (nodes: MenuTreeNodeWithAdmin[], level = 0): { item: MenuItemDto; level: number; hasChildren: boolean }[] => {
            const result: { item: MenuItemDto; level: number; hasChildren: boolean }[] = [];

            for (const node of nodes) {
                result.push({
                    item: node.item,
                    level,
                    hasChildren: node.children.length > 0,
                });

                // Always include children for drag & drop, regardless of expansion state
                if (node.children.length > 0) {
                    result.push(...flattenTree(node.children, level + 1));
                }
            }

            return result;
        },
        [], // Remove expandedItems dependency since we always include all items
    );

    const flatItems = useMemo(() => flattenTree(props.menuTree), [flattenTree, props.menuTree]);
    const itemIds = useMemo(() => flatItems.map(({ item }) => item.id), [flatItems]);

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            if (!props.canModify) return;

            const { active, over } = event;

            if (!over || active.id === over.id) {
                return;
            }

            const activeIndex = itemIds.indexOf(active.id as number);
            const overIndex = itemIds.indexOf(over.id as number);

            if (activeIndex === -1 || overIndex === -1) {
                return;
            }

            // Get the items being moved
            const activeItem = flatItems[activeIndex];
            const overItem = flatItems[overIndex];

            // Only allow reordering within the same parent level
            const activeParentId = activeItem.item.parent?.id || null;
            const overParentId = overItem.item.parent?.id || null;

            if (activeParentId !== overParentId) {
                snackbar.setSnackbarProps({
                    type: "warning",
                    message: t(
                        "menuManagement.cannotMoveAcrossLevels" as any,
                        "Cannot move items between different menu levels",
                    ),
                });
                return;
            }

            // Don't allow moving an item onto itself
            if (activeItem.item.id === overItem.item.id) {
                return;
            }

            try {
                // Get all sibling items (same parent) sorted by current weight
                const siblingItems = flatItems
                    .filter((f) => (f.item.parent?.id || null) === activeParentId)
                    .sort((a, b) => a.item.weight - b.item.weight);

                // Find positions in the weight-sorted array
                const activePositionInSiblings = siblingItems.findIndex((f) => f.item.id === activeItem.item.id);
                const overPositionInSiblings = siblingItems.findIndex((f) => f.item.id === overItem.item.id);

                if (activePositionInSiblings === -1 || overPositionInSiblings === -1) {
                    return;
                }

                // Calculate new order: move active item to over position
                const newSiblingOrder = [...siblingItems];
                const [movedItem] = newSiblingOrder.splice(activePositionInSiblings, 1);
                newSiblingOrder.splice(overPositionInSiblings, 0, movedItem);

                // Check if order actually changed
                const originalOrderIds = siblingItems.map((f) => f.item.id);
                const newOrderIds = newSiblingOrder.map((f) => f.item.id);

                if (JSON.stringify(originalOrderIds) === JSON.stringify(newOrderIds)) {
                    return;
                }

                // Create reorder data with new weights based on position
                const reorderData = {
                    items: newSiblingOrder.map((flatItem, index) => ({
                        id: flatItem.item.id,
                        weight: (index + 1) * 10, // New weight: 10, 20, 30, 40...
                        parentId: activeParentId,
                    })),
                };

                await props.onReorder(reorderData);
                snackbar.setSnackbarProps({
                    type: "success",
                    message: t("menuManagement.reorderSuccess" as any),
                });
            } catch (error) {
                snackbar.setSnackbarProps({
                    type: "error",
                    message: t("menuManagement.reorderError" as any),
                });
            }
        },
        [itemIds, flatItems, props.canModify, props.onReorder, snackbar, t],
    );

    if (props.menuTree.length === 0) {
        return <Alert severity="info">{t("menuManagement.noItems" as any)}</Alert>;
    }

    // Filter items for display based on expansion state
    const visibleItems = flatItems.filter(({ item, level }) => {
        if (level === 0) return true; // Always show root items

        // For child items, check if all parents are expanded
        let currentItem = item;
        while (currentItem.parent) {
            if (!props.expandedItems.has(currentItem.parent.id)) {
                return false; // Parent not expanded, hide this item
            }
            // Find the parent item to continue checking up the chain
            const parentItem = flatItems.find((f) => f.item.id === currentItem.parent?.id)?.item;
            if (!parentItem) break;
            currentItem = parentItem;
        }
        return true;
    });

    return (
        <DndContext
            sensors={props.canModify ? sensors : []}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                <Box>
                    {visibleItems.map(({ item, level, hasChildren }) => (
                        <SortableMenuItem
                            key={item.id}
                            item={item}
                            level={level}
                            hasChildren={hasChildren}
                            isExpanded={props.expandedItems.has(item.id)}
                            onToggleExpanded={props.onToggleExpanded}
                            onEditItem={props.onEditItem}
                            onDeleteItem={props.onDeleteItem}
                            canModify={props.canModify}
                            canDelete={props.canDelete}
                        />
                    ))}
                </Box>
            </SortableContext>
        </DndContext>
    );
};
